import { HttpContext, HttpContextToken, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, from, switchMap, throwError } from 'rxjs';
import { TokenService, BORNE_AUTH_PATH } from '../auth/token.service';

const RETRIED_AFTER_REFRESH = new HttpContextToken(() => false);

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const tokenService = inject(TokenService);

  // /borne/login is a public endpoint — pass through without auth
  if (req.url.includes(BORNE_AUTH_PATH)) {
    return next(req);
  }

  const token    = tokenService.getToken();
  const outgoing = token ? withAuthHeader(req, token) : req;

  return next(outgoing).pipe(
    catchError(err => {
      const alreadyRetried = req.context.get(RETRIED_AFTER_REFRESH);
      if (err.status === 401 && token && !alreadyRetried) {
        return from(tokenService.refreshToken()).pipe(
          switchMap(success => {
            if (!success) return throwError(() => err);
            const fresh = tokenService.getToken();
            const retryReq = withAuthHeader(req, fresh ?? '', new HttpContext().set(RETRIED_AFTER_REFRESH, true));
            return next(retryReq);
          })
        );
      }
      if (err.status === 401) {
        tokenService.clearToken();
      }
      return throwError(() => err);
    })
  );
};

function withAuthHeader(
  req: Parameters<HttpInterceptorFn>[0],
  token: string,
  context?: HttpContext
): Parameters<HttpInterceptorFn>[0] {
  return req.clone({
    setHeaders: { 'X-Authorization-Token': `Bearer ${token}` },
    ...(context ? { context } : {}),
  });
}
